import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { Loader2, Upload, Download, FileSpreadsheet, AlertCircle, CheckCircle2, Info } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';

const IMPORT_TYPES = [
  { value: 'crm_entities',           label: 'CRM Entities (Accounts & Contacts)' },
  { value: 'horses',                  label: 'Horses' },
  { value: 'farmfolio_care_catalog',  label: 'FarmFolio Care Catalog' },
  { value: 'farmfolio_care_log',      label: 'FarmFolio Care Log' },
];

// ─── CRM Template ────────────────────────────────────────────────────────────
const CRM_HEADERS = [
  // REQUIRED
  'crm_class',          // account | contact  (REQUIRED)
  'crm_type',           // organization | person  (REQUIRED)
  // Person-specific
  'person_prefix',      // Mr | Mrs | Ms | Dr | etc.
  'person_first_name',
  'person_last_name',
  'person_suffix',      // Jr | Sr | III | etc.
  'person_preferred_name',
  'person_salutation',  // Dear John
  'person_gender',
  'person_birthday',    // YYYY-MM-DD
  'person_clothing_size',
  'person_spouse_children_names',
  'person_alma_mater',
  'person_sports_teams',
  'person_cuisine_preferences',
  'person_drink_preferences',
  'person_hobbies',
  'person_industry_role', // owner | trainer | vet | farrier | agent | consignor | breeder | buyer | other
  'person_other_notes',
  'additional_identifier',
  // Organization-specific
  'organization_name',
  'organization_dba',
  'organization_website',
  'organization_industry_role', // owner | trainer | vet | farm | agency | media | other
  // Account flags (crm_class=account only)
  'account_active',       // TRUE | FALSE
  'account_rating',       // A | B | C | D | unrated
  'account_manager_email',  // email of user to assign as account manager
  'account_admin_email',    // email of user to assign as account admin
  // Contact details
  'primary_email',
  'primary_phone',
  'address_street',
  'address_city',
  'address_state',
  'address_zip',
  'address_country',
  // General
  'contact_preferences',
  'timezone',
  'sales_preferences',
  'notes',
  // Goals
  'overall_goals',              // comma-separated: race,sell,breed,show,other
  'breeding_goal_race',         // TRUE | FALSE
  'breeding_goal_sell',
  'number_of_mares_current',
  'number_of_mares_goal',
  'goal_mare_portfolio_age_low',
  'goal_mare_portfolio_age_high',
  'goal_mare_portfolio_stakes_winner',
  'goal_mare_portfolio_stakes_producer',
  'goal_mare_portfolio_foals_in_front_low',
  'goal_mare_portfolio_foals_in_front_high',
  'to_sell_weanling',
  'to_sell_yearling',
  'to_sell_2yo',
  'to_sell_broodmare',
  'to_sell_racing_prospect',
  'to_sell_hora',
  'willing_to_sell_privately',
  'willing_to_sell_partial_interest',
  'sells_at',             // comma-separated: Keeneland|Fasig-Tipton|OBS|etc.
  'sells_through',
  'sells_at_other',
  'interested_in_pinhook_partnerships',
  'interested_in_racehorse_partnerships',
  'interested_in_mare_partnerships',
  'interested_in_shares',
  'interested_in_breeding_rights',
  'seasons_booked_by',
  'season_fee_currency',  // USD | AUD | GBP | EUR
  'season_fee_range_low',
  'season_fee_range_high',
  'buy_season_no_guarantee',
  'racing_who_breaks_horses',
  'racing_preferred_circuit',
  'racing_willing_to_buy_interest',
  'racing_other_top_horses',
  'goals_other_notes',
  // Financial
  'commission_currency',
  'commission_rate_percent',
  'minimum_commission_amount',
  'commission_split',
  'commission_split_with',
  'commission_split_percent_entity',
  'commission_split_percent_linked',
  'withdrawal_fee_shipin',
  'withdrawal_fee_sale',
  'expense_markup_percent',
  'other_financial_notes',
];

const CRM_EXAMPLE_ORG_ACCOUNT = {
  crm_class: 'account', crm_type: 'organization',
  organization_name: 'Bluegrass Farms LLC', organization_dba: '', organization_website: 'www.bluegrassfarms.com', organization_industry_role: 'consignor',
  account_active: 'TRUE', account_rating: 'A', account_manager_email: 'admin@bloodstock.com', account_admin_email: '',
  primary_email: 'info@bluegrassfarms.com', primary_phone: '555-0100',
  address_street: '123 Horse Lane', address_city: 'Lexington', address_state: 'KY', address_zip: '40509', address_country: 'USA',
  contact_preferences: 'Prefers email', timezone: 'America/New_York', sales_preferences: 'Yearlings only', notes: 'Top tier consignor',
  breeding_goal_sell: 'TRUE', to_sell_yearling: 'TRUE', to_sell_weanling: 'TRUE', willing_to_sell_privately: 'TRUE',
  sells_at: 'Keeneland,Fasig-Tipton', commission_currency: 'USD', commission_rate_percent: '5', expense_markup_percent: '0',
};

const CRM_EXAMPLE_PERSON_CONTACT = {
  crm_class: 'contact', crm_type: 'person',
  person_prefix: 'Mr', person_first_name: 'James', person_last_name: 'Henderson', person_suffix: '',
  person_preferred_name: 'Jim', person_salutation: 'Dear Jim', person_gender: 'Male',
  person_birthday: '1975-06-15', person_clothing_size: 'L', person_industry_role: 'trainer',
  person_sports_teams: 'University of Kentucky', person_cuisine_preferences: 'Southern BBQ', person_drink_preferences: 'Bourbon',
  person_hobbies: 'Golf, Fishing',
  primary_email: 'jim.henderson@example.com', primary_phone: '555-0201',
  address_street: '456 Main St', address_city: 'Louisville', address_state: 'KY', address_zip: '40202', address_country: 'USA',
  contact_preferences: 'Call before 5pm', notes: 'Experienced trainer – 20 yrs',
  overall_goals: 'race,sell', racing_preferred_circuit: 'Churchill Downs',
};

// ─── Horse Template ───────────────────────────────────────────────────────────
const HORSE_HEADERS = [
  // REQUIRED
  'sex',        // male | female  (REQUIRED)
  'birthdate',  // YYYY-MM-DD  (REQUIRED)
  // Identity
  'name',                     // leave blank if unnamed=TRUE
  'unnamed',                  // TRUE | FALSE
  'color',                    // bay|black|chestnut|dark_bay_brown|gray_roan|palomino|white
  'microchip_id',
  'jockey_club_registration', // registration number
  // Pedigree
  'sire_name',
  'dam_name',
  'dam_year_foaled',
  'sire_grandsire_name',
  'sire_granddam_name',
  'dam_grandsire_name',
  'dam_granddam_name',
  'dam_granddam_year_foaled',
  'pedigree_reference_link',
  'other_pedigree_notes',
  // Area foaled
  'area_foaled',  // USA|AUS|NZ|GB|IRE|FR|JP|other
  // Location
  'boarding_location',
  'boarding_type',
  // Physical / gender
  'castrated',
  'cryptorchid',
  'horse_type_male',    // stallion|colt|rig|gelding|ridgling  (male only)
  'horse_type_female',  // mare|filly|broodmare|racing_mare|racing_filly  (female only)
  'cover_status',       // in_foal|not_in_foal|barren|maiden|unknown  (female only)
  // Nursing / early life
  'still_nursing',
  'separated_from_dam',
  // Breeding
  'bred_and_raised',
  'being_bred',
  'standing_at_stud',
  // Engagements
  'engagements_breeders_cup',
  'engagements_ntra',
  'engagements_ebf',
  'engagements_ktdf',
  // Vices
  'vices_cribber',
  'vices_weaving',
  'vices_stall_walking',
  'vices_pawing',
  'vices_wood_chewing',
  'vices_wall_kicking',
  // Other issues
  'other_issues_eye_certification',
  'other_issues_surgery',
  'vices_issues_notes',
  // Sale / status
  'nominated_for_sale',
  'entered_in_sale',
  'graduate',
  'top_horse',
  'other_notes',
];

const HORSE_EXAMPLE_MALE = {
  sex: 'male', birthdate: '2020-03-15', name: 'Thunder Valley', unnamed: 'FALSE',
  color: 'bay', microchip_id: '123456789ABC', jockey_club_registration: 'USA123456',
  sire_name: 'Storm Cat', dam_name: 'Golden Promise', dam_year_foaled: '2008',
  sire_grandsire_name: 'Storm Bird', sire_granddam_name: 'Terlingua',
  dam_grandsire_name: 'Mr. Prospector', dam_granddam_name: 'Toll Booth',
  pedigree_reference_link: 'https://www.equineline.com/...',
  area_foaled: 'USA', boarding_location: 'Claiborne Farm, KY', boarding_type: 'Full care',
  castrated: 'FALSE', cryptorchid: 'FALSE', horse_type_male: 'colt',
  still_nursing: 'FALSE', separated_from_dam: 'TRUE',
  bred_and_raised: 'TRUE', engagements_breeders_cup: 'TRUE', engagements_ntra: 'FALSE',
  engagements_ebf: 'FALSE', engagements_ktdf: 'FALSE',
  vices_cribber: 'FALSE', vices_weaving: 'FALSE', vices_stall_walking: 'FALSE',
  vices_pawing: 'FALSE', vices_wood_chewing: 'FALSE', vices_wall_kicking: 'FALSE',
  other_issues_eye_certification: 'FALSE', other_issues_surgery: 'FALSE',
  nominated_for_sale: 'TRUE', entered_in_sale: 'TRUE', graduate: 'FALSE', top_horse: 'FALSE',
  other_notes: 'Well-bred promising colt',
};

const HORSE_EXAMPLE_FEMALE = {
  sex: 'female', birthdate: '2018-05-20', name: 'Bella Rose', unnamed: 'FALSE',
  color: 'chestnut', microchip_id: 'DEF987654321', jockey_club_registration: 'USA789012',
  sire_name: 'Tapit', dam_name: 'Midnight Dream', dam_year_foaled: '2003',
  sire_grandsire_name: 'Pulpit', sire_granddam_name: 'Tap Your Heels',
  dam_grandsire_name: 'A.P. Indy', dam_granddam_name: 'Dream Deal',
  area_foaled: 'USA', boarding_location: 'Ocala Stud, FL', boarding_type: 'Broodmare care',
  horse_type_female: 'broodmare', cover_status: 'in_foal',
  still_nursing: 'FALSE', separated_from_dam: 'TRUE',
  being_bred: 'TRUE', bred_and_raised: 'FALSE',
  engagements_breeders_cup: 'FALSE', engagements_ntra: 'FALSE',
  engagements_ebf: 'FALSE', engagements_ktdf: 'FALSE',
  vices_cribber: 'FALSE', vices_weaving: 'FALSE', vices_stall_walking: 'FALSE',
  vices_pawing: 'FALSE', vices_wood_chewing: 'FALSE', vices_wall_kicking: 'FALSE',
  other_issues_eye_certification: 'TRUE', other_issues_surgery: 'FALSE',
  nominated_for_sale: 'FALSE', entered_in_sale: 'FALSE', graduate: 'TRUE', top_horse: 'TRUE',
  other_notes: 'Quality producing mare',
};

// ─── FarmFolio Care Catalog Template ─────────────────────────────────────────
const CARE_CATALOG_HEADERS = [
  // REQUIRED
  'category',       // Medical | Daily Care | Maintenance | Conditioning | Stats | Travel | Notes
  'type',           // e.g. Vaccination | Medication | Feed | Farrier | Exercise
  'name',           // item name
  // Dosage / serving
  'dosage',         // e.g. 10
  'dosage_unit',    // e.g. ml | mg | cc | oz
  'units',          // numeric quantity
  'serving_size',
  'serving_size_units',
  // Scheduling
  'routine',         // TRUE | FALSE
  'frequency_type',  // daily | twice_daily | weekly | monthly | annually | custom
  // Billing model
  'pricing_type',    // vendor_cost (farm pays vendor, passes cost through) | internal_service (farm charges a flat rate)
  'is_billable',     // TRUE | FALSE — whether this item generates an invoice line
  'account_rate',    // rate charged to the owner — used when pricing_type=internal_service
  'internal_cost',   // farm's own cost — used when pricing_type=vendor_cost (pass-through)
  'cost_source',     // farm_paid | direct_vendor_billed
  'cost_currency',   // USD | AUD | GBP | EUR
  'markup_strategy', // account_default | custom_markup
  'custom_markup_percent', // numeric — only applies when markup_strategy=custom_markup
  // Other
  'notes',
  'is_breeding',     // TRUE | FALSE
];

const CARE_CATALOG_EXAMPLE_MED = {
  category: 'Medical', type: 'Vaccination', name: 'Rabies Booster',
  dosage: '2', dosage_unit: 'ml', units: '', serving_size: '', serving_size_units: '',
  routine: 'FALSE', frequency_type: 'annually',
  pricing_type: 'vendor_cost',
  is_billable: 'TRUE', account_rate: '', internal_cost: '25.00',
  cost_source: 'farm_paid', cost_currency: 'USD',
  markup_strategy: 'account_default', custom_markup_percent: '',
  notes: 'Annual rabies vaccination', is_breeding: 'FALSE',
};

const CARE_CATALOG_EXAMPLE_FEED = {
  category: 'Daily Care', type: 'Feed', name: 'AM Grain',
  dosage: '', dosage_unit: '', units: '2', serving_size: '2', serving_size_units: 'lbs',
  routine: 'TRUE', frequency_type: 'daily',
  pricing_type: 'internal_service',
  is_billable: 'TRUE', account_rate: '3.50', internal_cost: '',
  cost_source: 'farm_paid', cost_currency: 'USD',
  markup_strategy: 'account_default', custom_markup_percent: '',
  notes: 'Morning grain ration', is_breeding: 'FALSE',
};

// ─── FarmFolio Care Log Template ──────────────────────────────────────────────
const CARE_LOG_HEADERS = [
  // REQUIRED
  'horse_name',     // must match horse system name exactly (case-insensitive)
  'category',       // Medical | Daily Care | Maintenance | Conditioning | Stats | Travel | Notes
  'type',           // e.g. Vaccination | Medication | Feed | Farrier | Exercise
  'name',           // care item name
  'log_date',       // YYYY-MM-DD
  // Optional
  'log_time',       // HH:MM (24-hour)
  'dosage',
  'dosage_unit',
  'units',          // numeric quantity given
  'location_name',
  'performed_by',
  'performed_by_type', // In-house | Vet | Farrier | Trainer | Other
  'comments',
  'is_billable',    // TRUE | FALSE
  'cost',           // numeric cost for this specific log entry
  'cost_currency',  // USD | AUD | GBP | EUR
  'is_breeding',    // TRUE | FALSE — marks entry as breeding-related
  'publish_to_owner_portal', // TRUE | FALSE
];

const CARE_LOG_EXAMPLE_1 = {
  horse_name: 'Thunder Valley', category: 'Medical', type: 'Vaccination', name: 'Rabies Booster',
  log_date: '2026-06-15', log_time: '09:00',
  dosage: '2', dosage_unit: 'ml', units: '1',
  location_name: 'Barn A', performed_by: 'Dr. Smith', performed_by_type: 'Vet',
  comments: 'Annual vaccination administered without issue',
  is_billable: 'TRUE', cost: '25.00', cost_currency: 'USD', is_breeding: 'FALSE', publish_to_owner_portal: 'FALSE',
};

const CARE_LOG_EXAMPLE_2 = {
  horse_name: 'Bella Rose', category: 'Daily Care', type: 'Feed', name: 'AM Grain',
  log_date: '2026-06-15', log_time: '07:30',
  dosage: '', dosage_unit: '', units: '2',
  location_name: 'Stall 4', performed_by: 'Mike', performed_by_type: 'In-house',
  comments: '', is_billable: 'FALSE', cost: '', cost_currency: 'USD', is_breeding: 'FALSE', publish_to_owner_portal: 'FALSE',
};

function rowToCSV(headers, rowObj) {
  return headers.map(h => {
    const val = rowObj[h] ?? '';
    const s = String(val);
    return s.includes(',') || s.includes('"') || s.includes('\n')
      ? `"${s.replace(/"/g, '""')}"`
      : s;
  }).join(',');
}

function buildCSV(headers, examples) {
  const lines = [headers.join(','), ...examples.map(e => rowToCSV(headers, e))];
  return lines.join('\n');
}

// ─── Component ────────────────────────────────────────────────────────────────
const BulkImportTab = () => {
  const [tenants, setTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState('');
  const [importType, setImportType] = useState('');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState(null);

  useEffect(() => { fetchTenants(); }, []);

  const fetchTenants = async () => {
    try {
      const response = await api.get('/tenants');
      setTenants(response.data || []);
    } catch (error) {
      console.error('Error fetching tenants:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    if (e.target.files?.[0]) {
      setFile(e.target.files[0]);
      setResult(null);
    }
  };

  const handleDownloadTemplate = () => {
    if (!importType) { toast.error('Please select an import type first'); return; }
    let csv = '';
    let filename = '';
    if (importType === 'crm_entities') {
      csv = buildCSV(CRM_HEADERS, [CRM_EXAMPLE_ORG_ACCOUNT, CRM_EXAMPLE_PERSON_CONTACT]);
      filename = 'crm_entities_template.csv';
    } else if (importType === 'horses') {
      csv = buildCSV(HORSE_HEADERS, [HORSE_EXAMPLE_MALE, HORSE_EXAMPLE_FEMALE]);
      filename = 'horses_template.csv';
    } else if (importType === 'farmfolio_care_catalog') {
      csv = buildCSV(CARE_CATALOG_HEADERS, [CARE_CATALOG_EXAMPLE_MED, CARE_CATALOG_EXAMPLE_FEED]);
      filename = 'farmfolio_care_catalog_template.csv';
    } else if (importType === 'farmfolio_care_log') {
      csv = buildCSV(CARE_LOG_HEADERS, [CARE_LOG_EXAMPLE_1, CARE_LOG_EXAMPLE_2]);
      filename = 'farmfolio_care_log_template.csv';
    }
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    toast.success('Template downloaded');
  };

  const handleImport = async () => {
    if (!selectedTenant || !importType || !file) {
      toast.error('Please select tenant, import type, and file');
      return;
    }
    setUploading(true);
    setResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('tenant_id', selectedTenant);
      formData.append('import_type', importType);
      const response = await api.post('/data-import/bulk', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setResult(response.data);
      if (response.data.imported_count > 0) {
        toast.success(`Imported ${response.data.imported_count} record(s) successfully`);
      } else {
        toast.warning('No records were imported. Check errors below.');
      }
      setFile(null);
      document.getElementById('bulk-import-file').value = '';
    } catch (error) {
      console.error('Error importing data:', error);
      toast.error(error.response?.data?.detail || 'Import failed');
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  const fieldCount = importType === 'crm_entities' ? CRM_HEADERS.length
    : importType === 'horses' ? HORSE_HEADERS.length
    : importType === 'farmfolio_care_catalog' ? CARE_CATALOG_HEADERS.length
    : importType === 'farmfolio_care_log' ? CARE_LOG_HEADERS.length
    : 0;

  const getHeadersForType = (t) => {
    if (t === 'crm_entities') return CRM_HEADERS;
    if (t === 'horses') return HORSE_HEADERS;
    if (t === 'farmfolio_care_catalog') return CARE_CATALOG_HEADERS;
    if (t === 'farmfolio_care_log') return CARE_LOG_HEADERS;
    return [];
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="font-serif text-xl">Bulk Data Import</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <p className="text-sm text-muted-foreground">
            Import CRM entities, horses, or FarmFolio data from CSV or Excel (.xlsx) files into a tenant account.
            Download a template to see all available fields with example data.
          </p>

          {/* Tenant */}
          <div className="space-y-2">
            <Label>Select Tenant *</Label>
            <Select value={selectedTenant} onValueChange={setSelectedTenant}>
              <SelectTrigger><SelectValue placeholder="Choose tenant..." /></SelectTrigger>
              <SelectContent>
                {tenants.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {/* Import type */}
          <div className="space-y-2">
            <Label>Import Type *</Label>
            <Select value={importType} onValueChange={(v) => { setImportType(v); setResult(null); }}>
              <SelectTrigger><SelectValue placeholder="Choose data type..." /></SelectTrigger>
              <SelectContent>
                {IMPORT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {/* Template download + field info */}
          {importType && (
            <div className="p-4 bg-muted/50 rounded-lg space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Download Template</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Includes all {fieldCount} available fields with 2 example rows
                  </p>
                </div>
                <Button variant="outline" size="sm" onClick={handleDownloadTemplate}>
                  <Download className="w-4 h-4 mr-2" /> Download CSV Template
                </Button>
              </div>

              {/* Field key info */}
              <div className="flex items-start gap-2 text-xs text-muted-foreground border-t pt-3">
                <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <div>
                  {importType === 'crm_entities' ? (
                    <>
                      <strong>Required:</strong> <code>crm_class</code> (account|contact) and <code>crm_type</code> (organization|person).{' '}
                      Person fields use the <code>person_</code> prefix. Organization fields use <code>organization_</code> prefix.{' '}
                      Managers can be assigned by email via <code>account_manager_email</code>.
                    </>
                  ) : importType === 'horses' ? (
                    <>
                      <strong>Required:</strong> <code>sex</code> (male|female) and <code>birthdate</code> (YYYY-MM-DD).{' '}
                      Leave <code>name</code> blank and set <code>unnamed=TRUE</code> for unnamed horses.{' '}
                      Female-only fields: <code>horse_type_female</code>, <code>cover_status</code>.
                    </>
                  ) : importType === 'farmfolio_care_catalog' ? (
                    <>
                      <strong>Required:</strong> <code>category</code>, <code>type</code>, and <code>name</code>.{' '}
                      Valid categories: <code>Medical | Daily Care | Maintenance | Conditioning | Stats | Travel | Notes</code>.{' '}
                      Set <code>is_billable=TRUE</code> with <code>account_rate</code> (internal_service) or <code>internal_cost</code> (vendor_cost) to auto-create an invoice standard item.{' '}
                      <code>pricing_type</code>: <code>vendor_cost</code> (pass-through cost) | <code>internal_service</code> (flat rate charged to owner).
                    </>
                  ) : (
                    <>
                      <strong>Required:</strong> <code>horse_name</code> (must match the horse's system name exactly, case-insensitive),{' '}
                      <code>category</code>, <code>type</code>, <code>name</code>, and <code>log_date</code> (YYYY-MM-DD).{' '}
                      <code>performed_by_type</code> options: <code>In-house | Vet | Farrier | Trainer | Other</code>.{' '}
                      Use <code>is_billable=TRUE</code> with <code>cost</code> to flag billable entries.
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* File upload */}
          <div className="space-y-2">
            <Label>Upload File *</Label>
            <div className="flex items-center gap-2">
              <div className="flex-1 border rounded-md px-3 py-2 text-sm text-muted-foreground bg-muted/30">
                {file ? (
                  <span className="flex items-center gap-2 text-foreground">
                    <FileSpreadsheet className="w-4 h-4 text-primary" /> {file.name}
                  </span>
                ) : 'No file selected...'}
              </div>
              <Button variant="outline" onClick={() => document.getElementById('bulk-import-file').click()}>
                Choose File
              </Button>
            </div>
            <input id="bulk-import-file" type="file" accept=".csv,.xlsx,.xls" onChange={handleFileSelect} className="hidden" />
            <p className="text-xs text-muted-foreground">Accepted: CSV, Excel (.xlsx, .xls). First row must be column headers.</p>
          </div>

          {/* Import button */}
          <div className="pt-2 border-t">
            <Button onClick={handleImport} disabled={uploading || !selectedTenant || !importType || !file}>
              {uploading
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Importing...</>
                : <><Upload className="w-4 h-4 mr-2" /> Import Data</>}
            </Button>
          </div>

          {/* Results */}
          {result && (
            <div className="border rounded-lg p-4 space-y-3">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-600" />
                  <span className="text-sm font-medium">{result.imported_count} imported</span>
                </div>
                {result.skipped_count > 0 && (
                  <div className="flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 text-amber-500" />
                    <span className="text-sm font-medium">{result.skipped_count} skipped</span>
                  </div>
                )}
              </div>
              {result.errors?.length > 0 && (
                <div className="space-y-1">
                  <p className="text-xs font-medium text-destructive">Errors (first 20):</p>
                  <div className="max-h-40 overflow-y-auto space-y-1">
                    {result.errors.map((e, i) => (
                      <p key={i} className="text-xs text-muted-foreground font-mono bg-muted/50 px-2 py-1 rounded">{e}</p>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Field reference */}
      {importType && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Field Reference — {
            importType === 'crm_entities' ? 'CRM Entities' :
            importType === 'horses' ? 'Horses' :
            importType === 'farmfolio_care_catalog' ? 'FarmFolio Care Catalog' :
            'FarmFolio Care Log'
          }</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-1.5">
              {getHeadersForType(importType).map(h => (
                <Badge key={h} variant="secondary" className="font-mono text-xs">{h}</Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default BulkImportTab;
